import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LADDER_LABEL_KEY } from '@d-orca/shared/client/activities';
import {
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  EyeIcon,
  HollowCircleIcon,
  MilestoneIcon,
  ProhibitedIcon,
  XCircleIcon,
  ZapIcon,
} from '@utils/icons';
import { tradeLabel } from '@utils/tradeLabel';
import { useSiteProfessions } from '@components/Pages/IssuesPage/hooks';
import { ProfessionChip } from '@components/ui/ProfessionChip/ProfessionChip';
import { LadderChip } from '../../SchedulerPage/components/LadderChip/LadderChip';
import { STATUS_OPTIONS, ACTIVITY_STATUS_OPTIONS } from '../activitiesFilter';
import type { useActivityFilters } from '../hooks/useActivityFilters';
import type { ActivityView } from '../hooks/useActivityView';
import { ISSUE_STATUSES, isStatusChanged, issueStatusKey } from '../pageFilters';
import { PLAN_STATUSES } from '../WorkPlan/workPlanModel';
import styles from './filterPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

/* Status glyphs reuse the app's status vocabulary: hollow/check circles from
   the dashboards' progress states, prohibited = blocked, eye = review. An
   activity's `done` and an issue's `completed` are the same fact per kind, so
   they share the check. Keyed by the option union: an offered status without
   an icon fails the build. */
const STATUS_ICONS: Record<(typeof STATUS_OPTIONS)[number], ReactNode> = {
  planned: <HollowCircleIcon size={14} />,
  forReview: <EyeIcon size={14} />,
  rejected: <XCircleIcon size={14} />,
  blocked: <ProhibitedIcon size={14} />,
  done: <CheckCircleIcon size={14} />,
  completed: <CheckCircleIcon size={14} />,
};

export interface IFilterPanelProps {
  open: boolean;
  onClose: () => void;
  f: ReturnType<typeof useActivityFilters>;
  /** The active view: the Progress Tracker is activities-only (its plan
   *  statuses, no Issues section); the Daily List and the Work Plan share the
   *  ladder's rungs and the issue statuses. */
  view: ActivityView;
  /** Distinct assignee trades present in the loaded schedule (already sorted
   *  by label) — the options for the Profession section. Empty = hide it. */
  professionOptions?: string[];
}

/** One Status checkbox, in the active view's vocabulary. */
interface IStatusRow {
  id: string;
  label: string;
  icon: ReactNode;
}

/** One collapsible panel section: header with an active-filter count badge,
 *  body hidden while collapsed (the badge keeps the state discoverable). The
 *  whole header is the collapse control, so the dead space between the title
 *  and the badge toggles too. */
function Section({
  title,
  activeCount,
  children,
}: {
  title: string;
  activeCount: number;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span className={`${styles.sectionChevron} ${collapsed ? styles.sectionChevronCollapsed : ''}`}>
          <ChevronDownIcon size={14} />
        </span>
        <span className={styles.sectionTitle}>{title}</span>
        {activeCount > 0 && <span className={styles.sectionCount}>{activeCount}</span>}
      </button>
      {!collapsed && <div className={styles.sectionBody}>{children}</div>}
    </section>
  );
}

function CheckRow({ label, icon, checked, onToggle }: { label: ReactNode; icon?: ReactNode; checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      className={`${styles.row} ${checked ? styles.rowSelected : ''}`}
      onClick={onToggle}
    >
      <span className={styles.box} aria-hidden="true">
        {checked && <CheckIcon size={11} />}
      </span>
      {icon && <span className={styles.rowIcon} aria-hidden="true">{icon}</span>}
      <span className={styles.rowLabel}>{label}</span>
    </button>
  );
}

/**
 * The filter control center: an in-flow sidebar on the page's LEFT edge
 * hosting EVERY filter as an always-visible section — the bar itself keeps
 * only the search and the trigger. Part of the page layout (not an overlay),
 * so the list stays fully visible and results narrow live as filters toggle;
 * the bar's trigger collapses it to reclaim the width.
 */
export function FilterPanel({ open, onClose, f, view, professionOptions = [] }: IFilterPanelProps) {
  const { t } = useTranslation();
  const { filters } = f;
  // Site profession catalog — supplies each trade's brand colour and its
  // contractor name for the shared ProfessionChip rows. Trades missing from
  // the catalog (or missing a colour) fall back to a plain text label,
  // mirroring ActivityCard.
  const { siteId } = useParams<{ siteId: string }>();
  const professionsQ = useSiteProfessions(siteId ?? null);
  const professionCatalog = professionsQ.data?.professions ?? [];
  // The Daily List and the Work Plan offer one vocabulary: an activity's
  // rung on the ladder, each marked with its own tone, and an issue's status,
  // keyed apart from the activities' (`planned` is an issue's "Open"). The
  // tracker keeps its plan statuses and no Issues section. Each box is checked
  // while its status shows; every status but done (and completed) shows by
  // default.
  const activityStatuses =
    view === 'tracker'
      ? ACTIVITY_STATUS_OPTIONS.map((s) => ({ id: s, label: t(`activities.status.${s}`), icon: STATUS_ICONS[s] }))
      : PLAN_STATUSES.map((rung) => ({ id: rung, label: t(LADDER_LABEL_KEY[rung]), icon: <LadderChip rung={rung} asSwatch /> }));
  const issueStatuses =
    view === 'tracker'
      ? []
      : ISSUE_STATUSES.map((s) => ({ id: issueStatusKey(s), label: t(`activities.pill.${s}`), icon: STATUS_ICONS[s] }));
  const hidden = filters.hiddenStatuses;
  const changedCount = (rows: IStatusRow[]) => rows.filter((s) => isStatusChanged(hidden, s.id)).length;
  const statusRows = (rows: IStatusRow[]) => (
    <div role="group" aria-label={t('activities.filter.status')}>
      {rows.map((s) => (
        <CheckRow
          key={s.id}
          label={s.label}
          icon={s.icon}
          checked={!hidden.includes(s.id)}
          onToggle={() => f.toggleStatus(s.id)}
        />
      ))}
    </div>
  );

  // No Escape handler: the sidebar is persistent page furniture, and stealing
  // Escape from popovers/dialogs layered above it would surprise.
  if (!open) return null;

  return (
    <aside className={styles.panel} aria-label={t('activities.filter.filters')}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>{t('activities.filter.filters')}</h2>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon size={16} />
        </button>
      </div>

      <ScrollArea className={styles.panelBody} viewportClassName={styles.panelContent}>
        {/* Professions lead the column — the trade is the first thing a site
            manager narrows by. The kind scope (All | Activities | Issues) is
            NOT here: it lives above the card list (KindScopeTabs), keeping
            this column a single checkbox grammar. */}
        {professionOptions.length > 0 && (
          <Section title={t('activities.filter.profession')} activeCount={filters.professions.length}>
            {professionOptions.map((tradeId) => {
              const selected = filters.professions.includes(tradeId);
              const text = tradeLabel(t, tradeId) || tradeId;
              const catalogEntry = professionCatalog.find((p) => p.tradeId === tradeId);
              return (
                <CheckRow
                  key={tradeId}
                  label={
                    catalogEntry?.backgroundColor ? (
                      /* Chip + gray contractor name beside it — the same row
                         anatomy as the Monitor page's Professions filter. */
                      <ProfessionChip
                        chip={{
                          background: catalogEntry.backgroundColor,
                          text,
                          tradeId,
                          ...(catalogEntry.contractor ? { contractor: catalogEntry.contractor } : {}),
                        }}
                      />
                    ) : (
                      text
                    )
                  }
                  checked={selected}
                  onToggle={() =>
                    f.setProfessions(
                      selected
                        ? filters.professions.filter((id) => id !== tradeId)
                        : [...filters.professions, tradeId],
                    )
                  }
                />
              );
            })}
          </Section>
        )}

        {/* Per-kind sections hold that kind's status checkboxes plus, for
            activities, the critical-path / milestone attributes. A badge
            counts the boxes moved off their default. */}
        <Section
          title={t('activities.filter.statusActivities')}
          activeCount={
            changedCount(activityStatuses) +
            (filters.criticalPath ? 1 : 0) +
            (filters.milestone ? 1 : 0) +
            (filters.checksOutstanding ? 1 : 0)
          }
        >
          {statusRows(activityStatuses)}
          {/* Hairline between the statuses and the attribute toggles — two
              different kinds of narrowing sharing one section. */}
          <div className={styles.rowDivider} aria-hidden="true" />
          <CheckRow
            label={t('activities.filter.criticalPath')}
            icon={<ZapIcon size={14} />}
            checked={filters.criticalPath}
            onToggle={f.toggleCriticalPath}
          />
          <CheckRow
            label={t('activities.filter.milestones')}
            icon={<MilestoneIcon size={14} />}
            checked={filters.milestone}
            onToggle={f.toggleMilestone}
          />
          <CheckRow
            label={t('activities.filter.checksOutstanding')}
            icon={<CheckCircleIcon size={14} />}
            checked={filters.checksOutstanding}
            onToggle={f.toggleChecksOutstanding}
          />
        </Section>

        {issueStatuses.length > 0 && (
          <Section title={t('activities.filter.statusIssues')} activeCount={changedCount(issueStatuses)}>
            {statusRows(issueStatuses)}
          </Section>
        )}

      </ScrollArea>
    </aside>
  );
}
